import {
	flexRender,
	getCoreRowModel,
	useReactTable,
} from '@tanstack/react-table'
import type { ColumnDef } from '@tanstack/react-table'
import type { ReactNode } from 'react'

import { EmptyState } from '@/components/common/empty-state'
import { Button } from '@/components/ui/button'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

interface DataTablePaginationProps {
	currentPage: number
	totalPages: number
	totalItems: number
	onPageChange: (page: number) => void
}

interface DataTableProps<TData> {
	columns: Array<ColumnDef<TData>>
	data: Array<TData>
	emptyMessage?: string
	pagination?: DataTablePaginationProps
	filterRow?: ReactNode
	className?: string
}

function DataTablePagination({ currentPage, totalPages, totalItems, onPageChange }: DataTablePaginationProps) {
	return (
		<div className="flex items-center justify-between space-x-2 py-4">
			<div className="flex items-center space-x-2 text-sm text-muted-foreground">
				<span>
					Page {currentPage} of {totalPages} ({totalItems} total)
				</span>
			</div>
			<div className="flex items-center space-x-2">
				<Button
					variant="outline"
					size="sm"
					onClick={() => onPageChange(Math.max(1, currentPage - 1))}
					disabled={currentPage === 1}
					className="h-8 rounded-md gap-1.5 px-3"
				>
					Previous
				</Button>
				<Button
					variant="outline"
					size="sm"
					onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
					disabled={currentPage >= totalPages}
					className="h-8 rounded-md gap-1.5 px-3"
				>
					Next
				</Button>
			</div>
		</div>
	)
}

export function DataTable<TData>({ columns, data, emptyMessage = 'No results found.', pagination, filterRow, className }: DataTableProps<TData>) {
	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
	})

	return (
		<div className={cn('bg-muted/30 rounded-lg p-6', className)}>
			<div className="relative w-full overflow-x-auto">
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id} className="hover:bg-muted/50 border-b transition-colors">
								{headerGroup.headers.map((header) => (
									<TableHead key={header.id} className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
										{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
									</TableHead>
								))}
							</TableRow>
						))}
						{filterRow}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows.length > 0 ? (
							table.getRowModel().rows.map((row) => (
								<TableRow key={row.id} className="hover:bg-muted/50 border-b transition-colors">
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id} className="p-2 align-middle whitespace-nowrap">
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow className="hover:bg-muted/50 border-b transition-colors">
								<TableCell colSpan={columns.length} className="p-2 align-middle">
									<EmptyState title={emptyMessage} />
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			{pagination && pagination.totalItems > 0 && <DataTablePagination {...pagination} />}
		</div>
	)
}
